import React, {
  useCallback, useContext, useEffect, useRef, useState
} from 'react';
import {
  extend,
  hoist,
  params as gqlParams,
  QueryContext,
  QueryProvider,
  number,
  string, shape, bool, useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { node } from 'prop-types';
import { PIPRedirector } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';

import { AisleBay } from '@thd-olt-component-react/aisle-bay';
// @TODO: James to look at multiple `clientOnlyProduct` firing (one w/ zip, one w/o) from buybox(?)
import { Buybox } from '@thd-olt-component-react/buybox';
import { CategoryList } from '@thd-olt-component-react/category-list';
import { CompareTray } from '@thd-olt-component-react/compare';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { Button, SkeletonLine } from '@one-thd/sui-atomic-components';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { MediaGalleryWithHotspotsCarousel } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductEntryMetadata } from '@thd-olt-component-react/metadata';
import { Promotion } from '@thd-olt-component-react/promotion';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import {
  parseUrl,
  productDefaultResultsDataModel,
  ProductResults,
  ProductResultsContext,
  ResultsApplied,
  ResultsDimensions, ResultsFilterAndSortDrawer,
  ResultsHeader,
  ResultsPagination,
  ResultsSortBy,
  ResultsWrapped,
  updateQueryParams,
  useProductResults
} from '@thd-olt-component-react/product-results';
import {
  DynamicRecsWrapper,
  RecentlyViewedWrapper,
  BuyItAgain
} from '@thd-olt-component-react/thd-recs-containers';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { OrderSample, usePLPSamples } from '@thd-olt-component-react/order-sample';
import { RoomVoInspire } from '@thd-olt-component-react/augmented-reality';
import { CustomProductPodResponsive } from '../browse-search/CustomProductPodResponsive';
import { Breadcrumbs, BreadcrumbsLoad, BreadcrumbsModel } from './dynamic-imports';
import { hasInStoreFilter } from '../../utils';
import { pepCampaignId, subscriptionType, requestKey } from '../../constants';
import '../../styles/pep-page.scss';
import { shouldDisplayResultsAppliedDesktop } from './pep-utils';
import { shouldDisplayVisuallySimilar } from '../product/product-utils';

const HoistedResultsWrapped = hoist(ResultsWrapped, CustomProductPodResponsive);

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};

ZoneCard.propTypes = {
  children: node.isRequired
};

export const Pep = () => {
  declareContexts([ProductResultsContext, QueryContext, ExperienceContext, CustomerContext]);
  const { isCustomerIdentified = false } = useThdCustomer();
  const SERVER_SIDE_PRODUCT_POD_COUNT = 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;
  const pageSize = 24;
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
  const ctx = useContext(ExperienceContext);
  const { clientStore, cookie } = ctx;
  const { itemId } = useParams();
  const { defaultVariables, isClientResolved } = useContext(QueryContext);
  const navigate = useNavigate();
  const { isLocalized, storeId, storeZip } = useStore({ varnish: false });
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;

  let fsMoreLikeThis = useConfigService('fs:isMoreLikeThisEnabled');
  const viewAllFilterButton = useConfigService('fs-prop:isViewAllFilterButtonEnabled');
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const isGetItFastOnPlp = useConfigService('fs-prop:isGetItFastOnPlp');
  const isFulfillmentRedesignEnabled = useConfigService('fs-prop:isFulfillmentRedesignEnabled');

  const scrollToRef = React.createRef();

  const decideFullWidth = isGetItFastOnPlp ? { xs: '12', sm: '12', md: '12', lg: '12' }
    : { xs: '8', sm: '9', md: '10', lg: '10' };

  function getResultsColProps(isFsDisableMfDimensions) {
    if (isGetItFastOnPlp) return { xs: '12', sm: '12', md: '12', lg: '12' };
    if (isFsDisableMfDimensions) return { xs: '12' };
    return {
      xs: '8',
      sm: '9',
      md: '10',
      lg: '10'
    };
  }

  const resultsColProps = getResultsColProps(useConfigService('fs-disable:mf-dimensions__mf-results-dimensions'));

  const onStoreChange = ({ output }) => {
    const { storeId: localStore } = output || {};
    if (!storeId) return;

    if (window && window.THD_LOCALIZER_AUTO_INIT && window.THD_LOCALIZER_AUTO_INIT.Localizer) {
      window.THD_LOCALIZER_AUTO_INIT.Localizer.localize({ storeId: localStore }).then(() => {
        window.location.reload();
      });
    }
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
  }, []);

  const { data: productData } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId
  });

  const { data: visualRecs } = useDataModel('recs', {
    variables: {
      anchorId: itemId,
      storeId,
      key: requestKey,
      zipCode: storeZip || '12345',
      apiName: 'visuallysimilar_1_0',
      skipInstallServices: true
    }
  });

  const targetPosition = (element) => {
    if (!element) return null;
    const currentPosition = window.scrollY || window.pageYOffset;
    return element.getBoundingClientRect().top + currentPosition;
  };

  const onDimensionsChange = useCallback(({ refinement, dimension = '' }) => {
    const { redirectUrl, url, clear = false, refinementKey } = refinement;
    const params = {
      ...parseUrl({ path }),
      ...parseUrl({ path: url }),
      Nao: 0
    };

    if (clear || (dimension && dimension?.label === 'Price' && !refinementKey)) {
      params.lowerbound = '';
      params.upperbound = '';
    }

    const paramsObject = Object.keys(params)
      .map((param) => {
        return {
          paramName: param,
          paramValue: params[param]
        };
      })
      .filter((par) => {
        return !/category|context|slugs/.test(par.paramName);
      });
    const updatedUrl = redirectUrl || updateQueryParams(path, paramsObject);
    window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    const targetNode = scrollToRef && scrollToRef.current ? scrollToRef.current : null;
    let targetLocation = targetPosition(targetNode);
    window.scrollTo({
      top: targetLocation,
      behavior: 'smooth'
    });
    navigate(updatedUrl);
  });

  const onPageChange = useCallback(({ page }) => {
    const newPath = updateQueryParams(path, [
      {
        paramName: 'Nao',
        paramValue: (page - 1) * pageSize
      }
    ]);

    navigate(newPath);

    const targetNode = scrollToRef && scrollToRef.current ? scrollToRef.current : null;
    let targetLocation = targetPosition(targetNode);
    window.scrollTo({
      top: targetLocation,
      behavior: 'smooth'
    });
  });

  const onSortChange = useCallback(({
    sortby, sortorder, name, currentPage, page
  }) => {
    const newPath = updateQueryParams(path, [
      {
        paramName: 'Nao',
        paramValue: null
      },
      {
        paramName: 'sortorder',
        paramValue: sortorder
      },
      {
        paramName: 'sortby',
        paramValue: sortby
      }
    ]);

    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    navigate(newPath);
  });

  const onChange = (dataSku = {}) => {
    const { slug, itemId: sskuItemId } = dataSku;
    if (slug && sskuItemId) {
      navigate(`/pep/${slug}/${sskuItemId}`, { state: { disableScroll: true } });
    }
  };

  const cleanPath = path.replace(/^\/render[^/]*/, '');

  const {
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    upperbound
  } = parseUrl({ path: cleanPath });

  const prcStoreId = useRef(storeId);
  const localizedAndClientLoaded = useRef(false);

  // show the sponsored products
  const additionalSearchParams = {
    sponsored: true, // ['bestmatch', 'topsellers'].indexOf(sortby) > -1
    deliveryZip: storeZip,
    mcvisId: cookie?.adobeCookie?.MCMID
  };

  let THDCustomer = {};

  if (clientStore) {
    let deliveryZipCode;
    if (
      typeof window !== 'undefined'
      && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
    ) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    additionalSearchParams.deliveryZip = deliveryZipCode?.zipcode;
    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }

  if (Object.keys(THDCustomer).length !== 0 && THDCustomer.svocID) {
    additionalSearchParams.svocID = THDCustomer.svocID;
  }

  const {
    data, error, loading, responseLoading, variables
  } = useProductResults({
    additionalSearchParams,
    itemId,
    keyword,
    lowerbound,
    nValue,
    pageSize,
    sortby,
    sortorder,
    productSSR: false,
    searchSSR: false,
    start: Nao,
    storeId,
    upperbound,
    isCustomerIdentified: isLocalized ? isCustomerIdentified : false,
  });

  if (data && !loading) {
    prcStoreId.current = variables.storeId;
    localizedAndClientLoaded.current = true;
  }

  const { products = [] } = data?.searchModel || {};
  const pipPath = pathname && pathname.replace(/\/pep\//gi, '/p/');
  const cleanedPath = (pathname || '').replace(/render-\w+\//, '');
  const isInStoreFilterON = hasInStoreFilter(data);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('pep.pip-ready', { itemId });
  }, [itemId]);

  const stringifyItemIds = (productsList) => {
    return productsList?.map(({ itemId: sku }) => sku).join('-');
  };

  useEffect(() => {
    if (data && !loading) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('pep.new-products', {
        ...data?.searchModel,
        defaultPageSize: pageSize
      });
    }
  }, [stringifyItemIds(products)]);

  const [selectedForCompare, setSelectedForCompare] = useState([]);

  const onSelectedForCompare = (id, selected = false) => {
    if (selected) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    }
  };

  const onCompare = () => {
    const queryparams = selectedForCompare.map((item, idx) => `item${idx + 1}=${item}`).join('&');
    if (cleanedPath) {
      window.sessionStorage.setItem('compareBaseURL', cleanedPath);
    }
    navigate(
      {
        pathname: '/compare',
        search: `?${queryparams}`
      },
      {
        bypassExperienceCheck: true
      }
    );
  };

  useEffect(() => {
    const itemIds = window.sessionStorage.getItem('compare');
    const compareBaseURL = window.sessionStorage.getItem('compareBaseURL');
    window.sessionStorage.removeItem('compareBaseURL');
    window.sessionStorage.removeItem('compare');
    if (itemIds && compareBaseURL === cleanedPath) {
      setSelectedForCompare(JSON.parse(itemIds));
    }
  }, []);

  const newDefaultVariables = {
    storeId,
    zipCode: storeZip,
    skipKPF: true,
    skipSubscribeAndSave: true,
    skipInstallServices: false,
    isBrandPricingPolicyCompliant: localizedAndClientLoaded.current ? isCustomerIdentified : false
  };
  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });
    if (attributes.product.fulfillment) {
      if (!isResolved) {
        return true;
      }
    }
    return skip;
  };
  const mountedFn = ({ mounted }) => {
    if (!isLocalized) return false;
    if (responseLoading) return false;
    return mounted;
  };

  /*
  Get Sample Products
  */
  const {
    loading: samplesLoading
  } = usePLPSamples({
    products,
    productsFinishedLoading: !data || loading,
    storeId,
    storeZip
  });
  // END: Get Sample Products

  const maxNumOfPodsToHydrate = 12;
  const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });

  const productsReduce = (productPods) => {
    let groupOfPods = 0;

    let reduced = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < maxNumOfPodsToHydrate) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === maxNumOfPodsToHydrate) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    if (productPods.length === SERVER_SIDE_PRODUCT_POD_COUNT && !isClientSearchModelResolved) {
      const placeholderHydrators = (CLIENT_SIDE_PRODUCT_POD_COUNT
        - SERVER_SIDE_PRODUCT_POD_COUNT) / maxNumOfPodsToHydrate;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        reduced.push(Array(maxNumOfPodsToHydrate).fill());
      }
    }

    return reduced;
  };
  const originalProducts = useRef(productsReduce(products));
  const hydrators = isClientSearchModelResolved || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;
  const fallbackHydrators = Array(2).fill();

  const hasDeliveryFilter = (breadCrumbs, filterLabelList) => {
    return (breadCrumbs || []).filter((breadCrumbDetail) => filterLabelList.includes(breadCrumbDetail?.label));
  };

  const applicableFilters = ['Same-Day Delivery', 'Next-Day Delivery'];
  const hasSameDayFilter = !!(hasDeliveryFilter(data?.searchModel?.taxonomy?.breadCrumbs, applicableFilters).length);

  return (
    <div className="pep-page">
      <Row className="isBound">
        <ErrorBoundary name="redirector">
          <PIPRedirector disableCanonicalRedirect disableCMMMCRedirect />
        </ErrorBoundary>
        <ErrorBoundary name="pep-metadata">
          <Metadata>
            <ProductEntryMetadata itemId={itemId} />
          </Metadata>
        </ErrorBoundary>
        <ErrorBoundary name="based-on-your-search">
          <Row>
            <Col className="based-on-your-search sui-py-2 sui-leading-none">Based on your search:</Col>
          </Row>
        </ErrorBoundary>
        <Row className="sui-flex sui-bg-subtle max-lg:!sui-max-h-none">
          <ErrorBoundary name="zone-a">
            <Row className="sui-flex sui-mb-4">
              <Col id="zone-a__col" className="sui-w-full sui-h-full sui-bg-primary sui-rounded-md">
                <ErrorBoundary id="zone-a-product" name="zone-a-product">
                  {/* eslint-disable-next-line max-len */}
                  <Col className="lg:sui-w-1/3 max-lg:sui-w-1/2 max-sm:sui-p-1 max-sm:sui-pb-0 sui-pr-2 sui-pl-1 sui-py-0">
                    <ErrorBoundary name="media">
                      <div className="sui-z-10 sui-absolute sui-p-1 sui-right-2">
                        <AddToList storeId={storeId} itemId={itemId} showIconButton showCount />
                      </div>
                      <MediaGalleryWithHotspotsCarousel
                        hover
                        itemId={itemId}
                        firstItemFetchPriority="high"
                        showDynamicPagination
                      />
                    </ErrorBoundary>
                  </Col>
                  {/* eslint-disable-next-line max-len */}
                  <Col className="lg:sui-w-1/3 max-lg:sui-w-1/2 max-sm:sui-flex max-sm:sui-flex-col max-sm:sui-mt-1 max-sm:sui-pt-1 max-sm:sui-pr-1">
                    <ErrorBoundary name="info">
                      <ErrorBoundary name="product-details">
                        <ProductDetails itemId={itemId}>
                          <div className="product-details__badge-title--wrapper">
                            <ProductDetails.Badge itemId={itemId} noPlaceholderClass />
                            <ProductDetails.Title link />
                          </div>
                          <ProductDetails.BrandCollection showCollection />
                          <ProductDetails.Ratings
                            offsetselector="#sticky-nav"
                            pageType="PEP"
                            qaselector="#questions_answers_full"
                            showBadge={false}
                            target="#ratings-and-reviews"
                            disableReviewsIfZero
                          />
                        </ProductDetails>
                      </ErrorBoundary>
                      <ErrorBoundary name="aisle-bay">
                        <AisleBay itemId={itemId} />
                      </ErrorBoundary>
                      <ErrorBoundary name="salient-points">
                        <SalientPoints hideIfMultiVariant itemId={itemId} max={3} />
                      </ErrorBoundary>
                      <ErrorBoundary name="super-sku">
                        <SuperSku itemId={itemId} onChange={onChange} />
                      </ErrorBoundary>
                      <ErrorBoundary name="order-sample">
                        <OrderSample itemId={itemId} link>
                          Need a closer look?
                        </OrderSample>
                      </ErrorBoundary>
                    </ErrorBoundary>
                  </Col>
                  <Col className="lg:sui-w-1/3 max-lg:sui-w-full max-sm:sui-py-0">
                    <ErrorBoundary name="checkout">
                      <ErrorBoundary name="price">
                        <Price
                          channel="desktop"
                          disableRangePricing
                          displayEachUom={false}
                          itemId={itemId}
                          large
                          clsRemediation={{
                            placeholders: true,
                            preservePlaceholders: true
                          }}
                        />
                      </ErrorBoundary>
                      <ErrorBoundary name="promotion">
                        <div className="sui-ml-1">
                          <Promotion channel="desktop" storeId={storeId} itemId={itemId} />
                        </div>
                      </ErrorBoundary>
                      <Col className="sui-p-0">
                        <ErrorBoundary name="buybox">
                          <div>
                            <Buybox
                              lite
                              noTopPadding
                              showBuyboxLiteQuantity
                              hideInstantCheckout
                              hidePaypalCheckout
                              itemId={itemId}
                            />
                          </div>
                          <ErrorBoundary name="details">
                            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                            <div className="sui-grid sui-ml-[-10px] sui-mr-[-10px] sui-w-full see-details">
                              <div className="sui-m-0 ">
                                <Button
                                  fullWidth
                                  variant="secondary"
                                  href={pipPath}
                                  target="_blank"
                                >
                                  View Full Product Details
                                </Button>
                              </div>
                            </div>
                          </ErrorBoundary>
                        </ErrorBoundary>
                      </Col>
                    </ErrorBoundary>
                  </Col>
                </ErrorBoundary>
              </Col>
            </Row>
            <Row className="sui-pt-2 sui-pb-6">
              <ErrorBoundary name="sbr-roomvo-inspire-banner">
                <RoomVoInspire />
              </ErrorBoundary>
            </Row>
            {shouldDisplayVisuallySimilar(productData) && visualRecs?.recs?.products?.length > 3
              && fsMoreLikeThis && (
              <DynamicRecsWrapper
                schemaName="visuallysimilar_1_0"
                anchorId={itemId}
                requestKey={requestKey}
                hideATC
                errorBoundary
                checkMinimumProducts
                miniumumProductCount={4}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'visuallysimilar_1_0',
                  delay: 1500,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            )}
            {(!shouldDisplayVisuallySimilar(productData) || visualRecs?.recs?.products?.length < 4 || !fsMoreLikeThis)
              && (
                <DynamicRecsWrapper
                  schemaName="pipsem"
                  anchorId={itemId}
                  hideATC
                  requestKey={requestKey}
                  errorBoundary
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard,
                    id: `pipsem-${itemId}`,
                    scrollBuffer: 0,
                    delay: 200
                  }}
                />
              )}
            <Col className="sui-ml-3">
              <ErrorBoundary name="breadcrumbs">
                <Hydrator
                  className="grid"
                  delay={3000}
                  id="pep-breadcrumbs"
                  scrollBuffer={1500}
                  waitFor={[BreadcrumbsLoad]}
                >
                  <Breadcrumbs data={data} error={error} loading={loading} hideOnNoCategory={false} />
                </Hydrator>
              </ErrorBoundary>
            </Col>
            <Col>
              <ErrorBoundary name="product-results">
                <span ref={scrollToRef} />
                <ProductResults data={data} queryVariables={variables} error={error} loading={loading}>
                  <Row>
                    <Col nopadding>
                      <Col>
                        <ErrorBoundary name="results-header">
                          {loading
                            ? <div className="sui-w-full"><SkeletonLine variant="heading" /></div>
                            : <ResultsHeader preamble="Similar" />}
                        </ErrorBoundary>
                      </Col>
                    </Col>
                  </Row>
                  {shouldDisplayResultsAppliedDesktop(ctx)
                    && !viewPrimaryFilters && (
                    <Row>
                      <ErrorBoundary name="results-applied">
                        <ErrorBoundary name="top-results-applied">
                          <Col sm="9">
                            <ResultsApplied
                              onAppliedChange={onDimensionsChange}
                              isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                            />
                          </Col>
                        </ErrorBoundary>
                        <Col xs="3">
                          <ResultsSortBy className="sui-flex sui-justify-end" onSortChange={onSortChange} />
                        </Col>
                      </ErrorBoundary>
                    </Row>
                  )}

                  <Row>
                    <ErrorBoundary
                      name="results-dimensions"
                      className="browse-search__left-outer-padding "
                    >
                      {!isGetItFastOnPlp && (
                        <ErrorBoundary name="mf-results-dimensions">
                          <Col lg="2" md="2" sm="3" xs="4" flatten>
                            <ResultsDimensions
                              enableMultiStore
                              enableVisualRefinement
                              onDimensionsChange={onDimensionsChange}
                              showOnlyGetItFast={viewAllFilterButton && viewPrimaryFilters}
                              isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                            />
                          </Col>
                        </ErrorBoundary>
                      )}
                      <Col
                        className="sui-p-0 sui-ml-3"
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...resultsColProps}
                      >
                        {isGetItFastOnPlp && (
                          <Row className="browse-search--no-left-margin">
                            <ErrorBoundary name="top-results-applied">
                              <Col sm="9">
                                <ResultsApplied
                                  onAppliedChange={onDimensionsChange}
                                  viewPrimaryFilters={viewPrimaryFilters}
                                  isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  isGetItFastOnPlp={isGetItFastOnPlp}
                                />
                              </Col>
                            </ErrorBoundary>
                          </Row>
                        )}
                        {viewPrimaryFilters && (
                          <>
                            <Row className="sui-ml-0">
                              <ErrorBoundary name="filter-and-sort-drawer">
                                <ResultsFilterAndSortDrawer
                                  onSortChange={onSortChange}
                                  onDimensionsChange={onDimensionsChange}
                                  disableStickyFilter
                                  excludePickUpToday
                                  hideViewAll={!viewAllFilterButton}
                                />
                              </ErrorBoundary>
                            </Row>
                          </>
                        )}

                        {(viewPrimaryFilters) && (
                          <>
                            <>
                              {isGetItFastOnPlp && (
                                <ErrorBoundary name="results-applied">
                                  <Row className="browse-search--no-left-margin sui-mt-3">
                                    <Col xs="9">
                                      <ErrorBoundary name="results-get-it-fast">
                                        <ResultsDimensions
                                          onDimensionsChange={onDimensionsChange}
                                          showOnlyGetItFast
                                          isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                          isGetItFastOnPlp={isGetItFastOnPlp}
                                          viewPrimaryFilters={viewPrimaryFilters}
                                        />
                                      </ErrorBoundary>
                                    </Col>
                                    <Col xs="3">
                                      <ResultsSortBy className="sui-flex sui-justify-end" onSortChange={onSortChange} />
                                    </Col>
                                  </Row>
                                </ErrorBoundary>
                              )}
                            </>
                            {!isGetItFastOnPlp && (
                              <Row className="browse-search--no-left-margin">
                                <ErrorBoundary name="results-applied">
                                  <ErrorBoundary name="top-results-applied">
                                    <Col sm="9">
                                      <ResultsApplied
                                        onAppliedChange={onDimensionsChange}
                                        viewPrimaryFilters={viewPrimaryFilters}
                                        isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      />
                                    </Col>
                                  </ErrorBoundary>
                                  <Col xs="3">
                                    <ResultsSortBy onSortChange={onSortChange} />
                                  </Col>
                                </ErrorBoundary>
                              </Row>
                            )}
                          </>
                        )}
                        <Row className="browse-search--no-left-margin">
                          {!isGetItFastOnPlp && (
                            <ErrorBoundary name="results-applied">
                              <Col flatten>
                                <Col sm="9">
                                  <ResultsApplied
                                    onAppliedChange={onDimensionsChange}
                                    isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  />
                                </Col>
                                <Col xs="3">
                                  <ResultsSortBy onSortChange={onSortChange} />
                                </Col>
                              </Col>
                            </ErrorBoundary>
                          )}
                          <ErrorBoundary name="wrapped-results">
                            <div className="grid">
                              {!hydrators?.length
                                ? fallbackHydrators.map((group) => {
                                  return <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />;
                                }) : (
                                  <QueryProvider
                                    cacheKey="pep-desktop-product-pods"
                                    dataSource="searchNav"
                                    defaultVariables={newDefaultVariables}
                                    mounted={mountedFn}
                                    skip={skipFn}
                                  >
                                    {hydrators?.map((group, hydratorIndex) => {
                                      if (!group?.length) {
                                        return <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />;
                                      }
                                      return (
                                        <Hydrator
                                          immediateRender={!hydratorIndex}
                                          className="grid"
                                          id={`browse-search-pods-${hydratorIndex + 1}`}
                                          key={`hydrator-${hydratorIndex}`}
                                          patch={!hydratorIndex}
                                          scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                                          placeholder={
                                            <ProductPodPlaceholder podsCount={SERVER_SIDE_PRODUCT_POD_COUNT} />
                                          }
                                        >
                                          <HoistedResultsWrapped>
                                            {group.map((product, itemIndex) => {
                                              const {
                                                itemId: skuId,
                                                canonicalUrl: skuUrl
                                              } = product?.identifiers || {};
                                              if (!skuId) return null;
                                              return (
                                                <ErrorBoundary
                                                  id={`pep-product-pod__${itemIndex}`}
                                                  index={itemIndex}
                                                  key={`${skuId}-${itemIndex}`}
                                                  name={`Product Pod: ${itemIndex}`}
                                                >
                                                  <Col
                                                    className="pep__pod"
                                                    fallback="6"
                                                    lg="3"
                                                    md="3"
                                                    sm="3"
                                                    xs="4"
                                                  >
                                                    <CustomProductPodResponsive
                                                      parent="pep"
                                                      itemId={skuId}
                                                      position={itemIndex}
                                                      storeId={storeId}
                                                      samplesLoading={samplesLoading}
                                                      hasSameDayFilter={hasSameDayFilter}
                                                      url={skuUrl}
                                                      hasInStoreFilter={isInStoreFilterON}
                                                      onCompare={onCompare}
                                                      onSelectedForCompare={onSelectedForCompare}
                                                      selectedForCompare={selectedForCompare}
                                                    />
                                                  </Col>
                                                </ErrorBoundary>
                                              );
                                            })}
                                          </HoistedResultsWrapped>
                                        </Hydrator>
                                      );
                                    })}
                                  </QueryProvider>
                                )}
                            </div>
                            <Row className="u--paddingTop u--paddingBottom">
                              <Col nopadding>
                                <Col>
                                  <ErrorBoundary name="results-pagination">
                                    <ResultsPagination
                                      onPageChange={onPageChange}
                                      pageSize={pageSize}
                                      path={path}
                                    />
                                  </ErrorBoundary>
                                </Col>
                                <Col>
                                  <ErrorBoundary name="search feedback">
                                    <SearchFeedback />
                                  </ErrorBoundary>
                                </Col>
                              </Col>
                            </Row>
                          </ErrorBoundary>
                        </Row>
                      </Col>
                    </ErrorBoundary>
                  </Row>
                </ProductResults>
              </ErrorBoundary>
            </Col>
            <Col>
              <ErrorBoundary name="promotional">
                <HdhomeSignup
                  formSubmission
                  subscriptionType={subscriptionType}
                  campaignId={pepCampaignId}
                />
              </ErrorBoundary>
            </Col>
            <RetailMediaCarousel
              errorBoundary
              name="sponsored-carousel-nucleus"
              breadCrumbs={data?.searchModel?.taxonomy?.breadCrumbs}
              categorySourceId={data?.searchModel?.metadata?.categoryID}
              nValue={nValue}
              pageContext={{
                schema: 'pip_sponsored',
                data: { itemId },
                isPip: true
              }}
              showSponsoredCarousel
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
            <QueryProvider
              defaultVariables={defaultVariables?.current}
              persist
              cacheKey="rv-pip-recs"
            >
              <RecentlyViewedWrapper
                schemaName="rv_gm_pip_rr"
                errorBoundary
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  id: 'rv_gm_pip_rr',
                  scrollBuffer: 1500,
                  delay: 3000
                }}
              />
            </QueryProvider>
            <Row className="u--paddingTop u--paddingBottom">
              <ErrorBoundary name="compare">
                <Col>
                  <CompareTray
                    itemIds={selectedForCompare}
                    onRemove={onSelectedForCompare}
                    onCompare={onCompare}
                  />
                </Col>
              </ErrorBoundary>
            </Row>
          </ErrorBoundary>
        </Row>
      </Row>
    </div>
  );
};

Pep.displayName = 'PepPage';

Pep.propTypes = {};

Pep.defaultProps = {};

const ProductSearchDataModel = {
  searchModel: gqlParams({ keyword: string(), navParam: string() }).shape({
    products: gqlParams({ startIndex: number() }).arrayOf(
      CustomProductPodResponsive.dataModel.product
    )
  })
};

Pep.dataModel = extend(
  {
    product: gqlParams({ itemId: string().isRequired }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
        })
      })
    })
  },
  PIPRedirector,
  AisleBay,
  BreadcrumbsModel,
  Buybox,
  CategoryList,
  ResultsDimensions,
  CompareTray,
  HdhomeSignup,
  MediaGalleryWithHotspotsCarousel,
  OrderSample,
  Price,
  PriceClearanceDataModel,
  Promotion,
  ProductDetails,
  AddToList,
  SalientPoints,
  SuperSku,
  productDefaultResultsDataModel,
  DynamicRecsWrapper,
  RecentlyViewedWrapper,
  ProductSearchDataModel,
  ResultsFilterAndSortDrawer
);
